'use client';

import { useEffect, useRef, useState, type ComponentType } from 'react';

type Loader = () => Promise<{ default: ComponentType }>;

interface LazyMountProps {
  /**
   * ВАЖНО: именно функция-загрузчик, а не готовый элемент/children. Если
   * передать сюда `<LeadForm/>` как children из серверного page.tsx, React
   * при пересечении Server→Client границы всё равно резолвит JS-модуль
   * LeadForm на клиенте (чтобы построить объект элемента), даже если этот
   * элемент ни разу не рендерится — next/dynamic() тогда выдаёт себя через
   * <script async> в HTML сразу при загрузке. `load` живёт целиком в клиентском
   * коде и вызывается только при visible=true — это единственный способ
   * реально отложить сетевой запрос чанка.
   */
  load: Loader;
  /** id элемента внутри загруженного компонента, на который ведут CTA-ссылки (например "lead" для `#lead`). */
  anchorId?: string;
  rootMargin?: string;
}

/**
 * Монтирует (и только тогда импортирует) компонент, когда обёртка
 * приближается к viewport — иначе его чанк конкурирует за сеть/main thread
 * с LCP-элементом Hero.
 */
export function LazyMount({ load, anchorId, rootMargin = '600px' }: LazyMountProps) {
  const ref = useRef<HTMLDivElement>(null);
  const loadRef = useRef(load);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;

    // rAF, а не прямой вызов в теле эффекта — setState здесь должен идти из
    // колбэка, а не синхронно из эффекта.
    // Любой hash при заходе на страницу (не только "свой" anchorId) значит,
    // что переход пришёл с другой страницы на секцию ниже по странице —
    // домонтировать все below-fold секции сразу же, а не по отдельности,
    // иначе секции выше цели доподгрузятся позже и вытолкнут её со scroll-позиции.
    const raf = requestAnimationFrame(() => {
      if (window.location.hash) setVisible(true);
    });

    const el = ref.current;
    const observer = el
      ? new IntersectionObserver(([entry]) => entry.isIntersecting && setVisible(true), { rootMargin })
      : undefined;
    if (el && observer) observer.observe(el);

    // Клик по любой якорной CTA (например "#lead" в Hero, "/#faq" в Navbar/Footer)
    // означает, что пользователь сейчас прыгнет мимо всех секций разом —
    // домонтировать все below-fold секции сразу, иначе те, что выше цели,
    // доподгрузятся уже после прыжка и вытолкнут её вниз из-под курсора.
    const onClick = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest?.('a[href*="#"]')) setVisible(true);
    };
    document.addEventListener('click', onClick);

    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
      document.removeEventListener('click', onClick);
    };
  }, [visible, anchorId, rootMargin]);

  const [Comp, setComp] = useState<ComponentType | null>(null);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    loadRef.current().then((mod) => {
      if (!cancelled) setComp(() => mod.default);
    });
    return () => {
      cancelled = true;
    };
  }, [visible]);

  useEffect(() => {
    // Триггерить по Comp, а не по visible: до resolve import() элемента с
    // anchorId ещё нет в DOM — scrollIntoView() на getElementById(null) молча
    // не сработает. Домонтировали по клику/хэшу раньше, чем секция попала в
    // viewport — нативный scroll-to-anchor к этому моменту уже не сработал.
    if (Comp && anchorId && window.location.hash === `#${anchorId}`) {
      const scrollToAnchor = () => document.getElementById(anchorId)?.scrollIntoView();
      requestAnimationFrame(scrollToAnchor);
      // Секции выше цели тоже домонтированы (см. эффект выше), но их чанки
      // могут дорезолвиться позже и сдвинуть цель уже после первого скролла —
      // подстраховочный повторный скролл ловит этот сдвиг.
      const timeout = setTimeout(scrollToAnchor, 400);
      return () => clearTimeout(timeout);
    }
  }, [Comp, anchorId]);

  return <div ref={ref}>{Comp && <Comp />}</div>;
}
